/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

import * as Auth from 'aws-amplify/auth';
import { commonConstants } from '../constants';
import { genPassword } from '../helpers';
import { postRequest } from '../helpers/apiUtils';

function logout() {
  return Auth.signOut({ global: true });
  // return Auth.signOut();
}

function getSession() {
  return Auth.fetchAuthSession();
}

function forgotPassword(username) {
  return Auth.resetPassword({ username });
}

// const getData = async () => {
//   const res = await axios.get('https://geolocation-db.com/json/');
//   return res.data.IPv4;
// };

const register = async (user) => {
  // Cognito SignUp requires password to be non-null; use generated password if missing (e.g. Microsoft flow)
  const password =
    user.password && String(user.password).trim() ? user.password : genPassword();
  return await Auth.signUp({
    username: user.username,
    password,
    attributes: {
      email: user.email,
      'custom:role': 'PLAN_CREATOR',
      'custom:msalIdtoken': user.msalIdtoken,
      'custom:userInfo': '77.77.77.77###' + genPassword(),
    },
  });
};

function verifyOtp(username, otp) {
  return Auth.confirmSignUp({ username, confirmationCode: otp });
}

async function login(user) {
  try {
    return await Auth.signIn({ username: user.username, password: user.password });
  } catch (error) {
    // Handle UserAlreadyAuthenticatedException
    if (error.name === 'UserAlreadyAuthenticatedException') {
      try {
        // Sign out the existing session
        await Auth.signOut();
        // Retry login after signing out
        return await Auth.signIn({ username: user.username, password: user.password });
      } catch (retryError) {
        // If retry fails, throw the retry error
        throw retryError;
      }
    }
    // Re-throw other errors
    throw error;
  }
}

async function resetPassword(username, otp, password) {
  try {
    const result = await Auth.confirmResetPassword({
      username,
      confirmationCode: otp,
      newPassword: password,
    });
    return result;
  } catch (error) {
    if (error.code) {
      // Handle error code if needed
      console.error('Error code:', error.code);
    }
  }
}

function confirmSignIn(user, code, mfaType) {
  return Auth.confirmSignIn(user, code, mfaType);
}

function completeNewPassword(user, newPassword) {
  return confirmSignIn({
    challengeResponse: newPassword,
  });
}

function getSesCognitoUserStatus(queryData) {
  let data = { body: queryData };
  return postRequest(
    commonConstants.USER_ACCOUNT_UTILS_API,
    '/user-account-utils/get-ses-cognito-user-status',
    data
  );
}

function updateCognitoUser(queryData) {
  let data = { body: queryData };
  return postRequest(
    commonConstants.USER_ACCOUNT_UTILS_API,
    '/user-account-utils/update_user_info',
    data
  );
}

function sendSMSOTP(queryData) {
  let data = { body: queryData };
  return postRequest(
    commonConstants.USER_ACCOUNT_UTILS_API,
    '/user-account-utils/2FA/send-otp-enc',
    data
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      throw error;
    });
}

function verifySMSOTP(queryData) {
  let data = { body: queryData };
  return postRequest(
    commonConstants.USER_ACCOUNT_UTILS_API,
    '/user-account-utils/2FA/verify-otp-enc',
    data
  );
}

export const accountService = {
  register,
  verifyOtp,
  login,
  forgotPassword,
  resetPassword,
  confirmSignIn,
  getSession,
  logout,
  completeNewPassword,
  getSesCognitoUserStatus,
  updateCognitoUser,
  sendSMSOTP,
  verifySMSOTP,
};
